<template>
  <Layout>
    <PageHeader
      :title="title"
      :subTitle="subTitle"
      :items="breadcrumbItems"
      hasBreadcrumb
      hasBackButton
    >
      <template #afterTitle>
        <div class="d-flex gap-2">
          <McButtonBack data-me="setting-vehicle-inspection-btn-back" href="/subsetting" />
          <McButton
            v-if="hasAccess('create')"
            data-me="setting-vehicle-inspection-btn-add"
            variant="primary"
            :text="$t('global.addField')"
            icon="icon-fill_plus"
            @click="handleAdd"
          />
        </div>
      </template>
    </PageHeader>

    <McGrapqlTable
      data-me="setting-vehicle-inspection-table"
      :apiClient="query"
      :fields="fields"
      trackBy="templateId"
      withCard
      searchable
      :isEmpty="noData"
      :customHandlers="customHandlers"
      :activeFilters="activeFilters"
      bulkDeleteApi
      hasFilter
      :hasDelete="false"
      :hasCheckbox="hasAccess('delete')"
    >
      <template #button-filter="{ isSelected, showDeleteModal }">
        <div v-if="!noData" class="d-flex justify-content-end gap-2">
          <McButton
            v-if="isSelected && hasAccess('delete')"
            data-me="setting-vehicle-inspection-btn-delete"
            variant="secondary-red"
            :text="$t('global.delete')"
            icon="icon-outline_trash"
            @click="showDeleteModal"
          />
          <McTableFilter
            data-me="setting-vehicle-inspection-table-filter"
            :buttonProps="{ 'data-me': 'setting-vehicle-inspection-btn-filter' }"
            :activeFilters="activeFilters"
            :parseFilterDataMappings="filterDataMappings"
            :presetFullObjects="filterFullObjects"
            @submit="handleApplyFilters"
            @getFullObjects="handleFullObjects"
          >
            <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
              <div class="mc-modal__filter-item">
                <FormulateInput
                  data-me="setting-vehicle-inspection-form-input-license-plate"
                  v-bind="vehicleFilterField"
                  :fullObject="fullObjects.licensePlate"
                  :value="formValues.licensePlate"
                  @update:formValues="updateFormValues({ key: 'licensePlate', value: $event })"
                  @update:fullObject="updateFullObjects({ key: 'licensePlate', value: $event })"
                />
              </div>
              <div class="mc-modal__filter-item">
                <FormulateInput
                  data-me="setting-vehicle-inspection-form-input-created-at"
                  v-bind="createdAtFilterField"
                  :value="formValues.createdAt"
                  value-type="YYYY-MM-DD HH:mm"
                  @input="updateFormValues({ key: 'createdAt', value: $event })"
                />
              </div>
            </template>
          </McTableFilter>
        </div>
      </template>
      <template #cell(vehicleTarget)="data">
        <span v-if="data.item.allVehicles === true">{{
          $t('vehicleInspection.fields.allVehicles')
        }}</span>
        <span v-else-if="data.item.totalVehicles > 0 && data.item.totalVgroups == 0"
          >{{ data.item.totalVehicles }} {{ $t('global.text.vehicle') }}</span
        >
        <span v-else-if="data.item.totalVgroups > 0 && data.item.totalVehicles == 0"
          >{{ data.item.totalVgroups }} {{ $t('global.field.vehicleGroup') }}</span
        >
      </template>
      <template #cell(description)="data">
        <span
          v-b-tooltip.hover.top="{
            title: data.item.description,
            disabled: !data.item.description || data.item.description.length <= 200,
          }"
          class="description-clamp"
        >
          {{ data.item.description || '-' }}
        </span>
      </template>
      <template #cell(createdAt)="data">
        <span>{{ momentDateShortMonth(data.value) }}</span>
        <br />
        <span class="text-muted">
          {{ momentTimeSeconds(data.value) }} {{ getLabelTimezone() }}
        </span>
      </template>
      <template #cell(updatedAt)="data">
        <span>{{ momentDateShortMonth(data.value) }}</span>
        <br />
        <span class="text-muted">
          {{ momentTimeSeconds(data.value) }} {{ getLabelTimezone() }}
        </span>
      </template>
      <template #emptyState>
        <McEmptyState
          data-me="setting-vehicle-inspection-empty-state"
          class="pb-4"
          :title="$t('vehicleInspection.templateEmptyState.title')"
          :desc="$t('master.service.emptyState.desc')"
          @buttonPrimaryAction="customHandlers.handleAddEmpty"
        />
      </template>
    </McGrapqlTable>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import i18n from '@/i18n'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McEmptyState from '@/components/McEmptyState'
import McTableFilter from '@/components/McTableFilter'
import { Helper } from '@/mixin/helper'
import { hasAccess } from '@/router/views/setting/template/vehicle-inspection/lib/utils'
import moment from 'moment'

import {
  GET_VEHICLE_FILTER,
  GET_VEHICLE_TYPES,
  GET_VEHICLE_GROUPS,
} from '@/services/api/master/fleet'

import {
  DELETE_VEHICLE_INSPECTION_TEMPLATE,
  GET_VEHICLE_INSPECTION_TEMPLATES,
} from '@/services/api/setting/vehicleInspection'

export default {
  page: {
    title: () => i18n.t('global.setting'),
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McGrapqlTable,
    McEmptyState,
    McTableFilter,
  },
  mixins: [Helper],
  apollo: {
    vehicleTypes: {
      query: GET_VEHICLE_TYPES,
      variables() {
        return {
          filter: {
            hasVehicle: true,
            isActive: true,
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        let vehicleTypes = { ...data.vehicleTypes }
        this.$store.commit('report/updateVehicleTypeList', {
          list: vehicleTypes.vehicleTypes.data,
          loaded: true,
        })
      },
    },
    vgroups: {
      query: GET_VEHICLE_GROUPS,
      result({ data, loading }) {
        if (loading) return
        let vgroups = { ...data.vgroups }
        this.$store.commit('report/updateVgroupList', {
          list: vgroups.vgroups,
          loaded: true,
        })
      },
    },
  },
  data() {
    return {
      noData: false,
      activeFilters: {
        vehicleIds: [],
        createdAt: [],
      },
      filterFullObjects: {
        licensePlate: [],
      },
      customHandlers: {
        handleAddEmpty: () => {
          const query = this.$route.query
          this.$router.push({ name: 'Create Setting Template Vehicle Inspection', query })
        },
        handleClickRow: (item) => {
          if (this.hasAccess('update')) {
            this.$router.push({
              name: 'Update Setting Template Vehicle Inspection',
              params: { templateId: item.templateId },
            })
          }
        },
      },
    }
  },
  computed: {
    title() {
      return this.$t('vehicleInspection.title')
    },
    subTitle() {
      return this.$t('vehicleInspection.subtitle')
    },

    vgroupList() {
      return this.$store.state.report.vGroupList
    },
    vehicleTypeList() {
      return this.$store.state.report.vehicleTypeList
    },
    breadcrumbItems() {
      return [
        {
          text: this.$t('setting.list.development.setting'),
          to: '/setting',
        },
        {
          text: this.$t('setting.homePage.template'),
          to: '/subsetting',
        },
        {
          text: this.$t('vehicleInspection.title'),
        },
      ]
    },
    query() {
      return {
        get: async (filter) => {
          return await this.getData(filter)
        },
        delete: async (templateIds) => {
          const res = await this.$apollo.mutate({
            mutation: DELETE_VEHICLE_INSPECTION_TEMPLATE,
            variables: {
              input: {
                templateIds: templateIds.map(Number),
              },
            },
          })
          const data = res.data.deleteVehicleInspectionTemplates

          if (!data.isSuccessful) {
            this.$mcToast.error(data.message || this.$t('global.somethingWentWrong'))
            return false
          }
          return {
            count: data.deleteVehicleInspectionTemplates.count,
          }
        },
      }
    },
    filterDataMappings() {
      return {
        licensePlate: 'vehicleIds',
        createdAt: 'createdAt',
      }
    },
    vehicleFilterField() {
      const vehicleFields = [
        {
          label: this.$t('global.field.licensePlate'),
          key: 'licensePlate',
          isSortable: true,
        },
        {
          label: this.$t('global.field.vehicleType'),
          key: 'vehicleType',
          isSortable: false,
          formatter: (value) =>
            value
              ? [value.manufacturer ?? '-', value.model ?? '-', value.type ?? '-'].join(' / ')
              : '-',
        },
        {
          label: this.$t('global.field.vehicleGroup'),
          key: 'vehicle2Vgroup',
          isSortable: false,
          formatter: (value) =>
            value?.length ? value.map((vehicle) => vehicle.vgroup.name).join(', ') : '-',
        },
      ]

      return {
        name: 'licensePlate',
        label: this.$t('global.field.licensePlate'),
        type: 'report-filter-value-help',
        placeholder: this.$t('global.form.select') + ' ' + this.$t('global.field.licensePlate'),
        modalTitle: this.$t('global.form.select') + ' ' + this.$t('global.field.licensePlate'),
        trackBy: 'id',
        displayedBy: 'licensePlate',
        listGetter: this.getVehicleList,
        multiple: true,
        tableFields: vehicleFields,
        filters: [
          {
            label: this.$t('global.field.vehicleType'),
            key: 'vehicleType',
            data: this.getFilterValue(1000, 'vehicleType'),
            model: [],
          },
          {
            label: this.$t('global.field.vehicleGroup'),
            key: 'vgroup',
            data: this.getFilterValue(1000, 'vgroup'),
            model: [],
          },
        ],
      }
    },
    createdAtFilterField() {
      return {
        name: 'createdAt',
        label: this.$t('global.field.createdDate'),
        type: 'date-picker',
        placeholder: this.$t('global.form.select') + ' ' + this.$t('global.field.createdDate'),
        dateFormat: 'YYYY-MM-DD HH:mm',
        datepickerType: 'datetime',
        range: true,
      }
    },
    fields() {
      return [
        {
          key: 'name',
          label: this.$t('vehicleInspection.fields.templateName'),
          isSortable: true,
          isRedirectable: true,
          thStyle: { width: '200px', minWidth: '200px' },
        },
        {
          key: 'vehicleTarget',
          label: this.$t('vehicleInspection.fields.vehicleTarget'),
          isSortable: false,
          thStyle: { width: '200px', minWidth: '200px' },
        },
        {
          key: 'description',
          label: this.$t('vehicleInspection.fields.description'),
          isSortable: false,
          thStyle: { width: '400px', minWidth: '400px' },
        },
        {
          key: 'createdAt',
          label: this.$t('vehicleInspection.fields.dateCreated'),
          isSortable: true,
          thStyle: { width: '100px', minWidth: '100px' },
        },
        {
          key: 'updatedAt',
          label: this.$t('vehicleInspection.fields.dateModified'),
          isSortable: true,
          thStyle: { width: '100px', minWidth: '100px' },
        },
      ]
    },
  },
  methods: {
    hasAccess(type) {
      return hasAccess(this.$store, type)
    },
    async getData(filter) {
      const { createdAt, vehicleIds } = this.activeFilters
      const [start, end] = createdAt || []

      const variablesFilter = { ...filter }

      if (start && end) {
        variablesFilter.createdAt = {
          start: moment(start).startOf('minute').toISOString(),
          end: moment(end).endOf('minute').toISOString(),
        }
      }

      if (vehicleIds.length) {
        variablesFilter.vehicleIds = vehicleIds
      }

      const res = await this.$apollo.query({
        query: GET_VEHICLE_INSPECTION_TEMPLATES,
        fetchPolicy: 'no-cache',
        variables: { filter: variablesFilter },
      })

      const data = res.data.vehicleInspectionTemplates
      if (!data.isSuccessful) {
        this.$mcToast.error(data.message || this.$t('global.error'))
        return null
      }

      const isInitialLoad = !filter.search && !start && !end && !vehicleIds.length
      this.noData = isInitialLoad && data.vehicleInspectionTemplates.data.length === 0

      return data.vehicleInspectionTemplates
    },
    async getVehicleList(opts) {
      const { orderBy, ...filter } = opts
      const res = await this.$apollo.query({
        query: GET_VEHICLE_FILTER,
        variables: {
          filter: {
            status: ['I', 'R', 'A'],
            parentOnly: true,
            orderBy: orderBy ?? JSON.stringify({ licensePlate: 'asc' }),
            ...filter,
          },
        },
      })

      return res.data.vehiclesFilter.vehiclesFilterPaginated
    },

    handleApplyFilters(filters) {
      this.activeFilters = filters.reduce(
        (activeFilters, { key, values }) => ({
          ...activeFilters,
          [key]: values || [],
        }),
        this.activeFilters
      )
    },
    handleFullObjects(fullObjects) {
      this.filterFullObjects = fullObjects
    },
    handleAdd() {
      const query = this.$route.query
      this.$router.push({ name: 'Create Setting Template Vehicle Inspection', query })
    },
    getListFilterLoaded(type) {
      let loaded = false
      let list = []
      switch (type) {
        case 'vehicleType':
          list = this.vehicleTypeList.list.map((item) => {
            return {
              id: item.vehicleTypeId,
              name: `${item.manufacturer} / ${item.model} / ${item.type}`,
            }
          })
          loaded = this.vehicleTypeList.loaded
          break
        case 'vgroup':
          list = this.vgroupList.list.map((item) => {
            return { id: item.id, name: item.name }
          })
          loaded = this.vgroupList.loaded
          break
        default:
          break
      }
      return { loaded, list }
    },
    getFilterValue(interval, type) {
      return new Promise((resolve) => {
        const { loaded, list } = this.getListFilterLoaded(type)
        if (!loaded) {
          const intervalId = setInterval(() => {
            const { loaded, list } = this.getListFilterLoaded(type)
            if (loaded) {
              clearInterval(intervalId)
              resolve(list)
            }
          }, interval)
        } else {
          resolve(list)
        }
      })
    },
  },
}
</script>

<style scoped>
.description-clamp {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}
</style>
